/**
 * Module dependencies.
 */

import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { useEffect, useRef, useState } from 'react';
import config from 'config';

/**
 * Types.
 */

export type CgChain = { key: string; label: string; family: string; nativeSymbol: string };

export type CgHolding = {
  assetId: number;
  amount: string;
  symbol: string;
  name: string | null;
  chain: string | null;
  logo: string | null;
  priceKey: string | null;
  priceUsd?: number;
  valueUsd?: number;
  rewardsAmount?: string | null;
};

export type CgWallet = {
  id: string;
  name: string;
  type: 'tracked' | 'manual' | 'exchange' | 'nexo';
  provider: string | null;
  chain: string | null;
  chainLabel: string | null;
  address: string | null;
  syncStatus: 'idle' | 'syncing' | 'ok' | 'error';
  syncError: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
  valueUsd?: number;
  /** Ledger entries + on-chain transactions for this wallet. */
  txCount?: number;
  /** Whether the wallet holds a non-zero balance. */
  hasHoldings?: boolean;
  holdings?: CgHolding[];
};

export type CgEntry = {
  id: string;
  assetId: number;
  symbol: string;
  logo: string | null;
  type: string;
  direction: 'in' | 'out';
  amount: string;
  unitPriceFiat: string | null;
  currency: 'USD' | 'EUR';
  feeFiat: string | null;
  timestamp: string;
  source: 'manual' | 'onchain';
  txHash: string | null;
  note: string | null;
  needsData?: boolean;
  aggregated?: boolean;
  aggregatedCount?: number | null;
  counterSymbol?: string | null;
};

export type CgOnchainTx = {
  id: number;
  assetId: number;
  symbol: string;
  logo: string | null;
  direction: 'in' | 'out';
  amount: string;
  unitPriceFiat: string | null;
  timestamp: string;
  source: string;
  txHash: string | null;
  linkedEntryId: string | null;
  linkedNeedsData?: boolean;
  confirmed: boolean;
};

export type CgTransaction = CgEntry & {
  walletId: string;
  walletName: string;
  walletType: 'tracked' | 'manual' | 'exchange' | 'nexo';
  currency: 'USD' | 'EUR';
  totalSpent: string | null;
  currentPriceUsd: number | null;
  unitPriceUsd: number | null;
  unitPriceEurAt: number | null;
  profitPct: number | null;
};

export type DisplayCurrency = string;

export type CgSettings = {
  displayCurrency: string;
  currencies: string[];
  valueInterval: string;
  priceInterval: string;
};

export type CgRates = { usdPerEur: number; rates: Record<string, number> };

export type CgAssetRow = {
  assetId: number;
  symbol: string;
  name: string | null;
  logo: string | null;
  chain: string | null;
  isStable: boolean;
  amount: string;
  price: number;
  valueUsd: number;
  avgCost: number | null;
  costBasisUsd: number | null;
  unrealizedPnlUsd: number | null;
  profitRate: number | null;
  allocationPct: number;
};

export type CgOverview = {
  totalValueUsd: number;
  totalInvestedUsd: number;
  totalPnlUsd: number;
  totalProfitRate: number | null;
  stableValueUsd: number;
  stablePct: number;
  assets: CgAssetRow[];
  topGainers: CgAssetRow[];
  topLosers: CgAssetRow[];
  walletBreakdown: CgWalletBreakdown[];
  walletHoldings: CgWalletHoldings[];
  needsDataCount: number;
  history: Array<{ timestamp: string; value: number }>;
};

export type CgWalletHoldings = {
  walletId: string;
  name: string;
  valueUsd: number;
  assets: CgAssetRow[];
};

export type CgWalletBreakdown = {
  walletId: string;
  name: string;
  valueUsd: number;
  allocationPct: number;
};

export type CgAssetWalletTx = {
  id: string;
  walletId: string;
  walletName: string;
  type: string;
  direction: 'in' | 'out';
  amount: string;
  unitPriceFiat: string | null;
  currency: 'USD' | 'EUR';
  totalSpent: string | null;
  timestamp: string;
  source: 'manual' | 'onchain';
  poolAssigned?: boolean;
  needsData?: boolean;
};

export type CgAssetDetail = Omit<CgAssetRow, 'allocationPct'> & {
  wallets: Array<{
    walletId: string;
    name: string;
    type: string | null;
    amount: string;
    avgCost: number | null;
    price: number;
    valueUsd: number;
    pnlUsd: number | null;
  }>;
  // Types + wallets present for this asset; rows are fetched paginated per wallet.
  txTypes: string[];
  txWallets: Array<{ walletId: string; name: string }>;
  priceHistory: Array<{ timestamp: number; price: number }>;
  valueHistory: Array<{ timestamp: string; value: number }>;
};

/**
 * Query keys.
 */

const KEY = 'cryptoGest';

/**
 * Chains supported by tracked wallets.
 */

export function useCryptoGestChains() {
  const client = useRequestProvider();

  return useQuery<CgChain[]>([KEY, 'chains'], {
    queryFn: async () => client.get(resolveAppUrl('cryptoGestChains')).then(({ data }: any) => data?.data ?? [])
  });
}

/**
 * WebSocket URL for the live price relay, derived from the API base URL.
 */

function priceStreamUrl(): string {
  const base: string = config?.api?.baseUrl || '/api';
  const path = '/ws/crypto-gest/prices';

  if (/^https?:\/\//.test(base)) {
    return base.replace(/^http/, 'ws') + path;
  }

  const proto = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = typeof window !== 'undefined' ? window.location.host : '';

  return `${proto}//${host}${base}${path}`;
}

/**
 * Live USD prices for a set of symbols, pushed from the server over WebSocket
 * (near-real-time). Returns a map of upper-cased symbol → USD price; empty
 * until the first tick arrives (callers fall back to their static price).
 */

export function useCryptoGestPriceStream(symbols: string[]): Record<string, number> {
  const [prices, setPrices] = useState<Record<string, number>>({});
  const socketRef = useRef<WebSocket | null>(null);
  const key = Array.from(new Set(symbols.map(s => s.toUpperCase())))
    .sort()
    .join(',');

  useEffect(() => {
    if (!key) {
      return;
    }

    let closed = false;
    let socket: WebSocket | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (closed) {
        return;
      }

      try {
        socket = new WebSocket(priceStreamUrl());
      } catch {
        return;
      }

      socketRef.current = socket;

      socket.onopen = () => socket?.send(JSON.stringify({ symbols: key.split(',') }));
      socket.onmessage = event => {
        try {
          const message = JSON.parse(event.data);

          if (message?.type === 'prices' && message.prices) {
            setPrices(prev => ({ ...prev, ...message.prices }));
          }
        } catch {
          // Ignore malformed frames.
        }
      };

      // Reconnect with a small delay while the component is mounted.
      socket.onclose = () => {
        if (!closed && !retry) {
          retry = setTimeout(() => {
            retry = null;
            connect();
          }, 4000);
        }
      };
    };

    connect();

    return () => {
      closed = true;

      if (retry) {
        clearTimeout(retry);
      }

      socket?.close();
      socketRef.current = null;
    };
  }, [key]);

  return prices;
}

/**
 * A candidate internal transfer between two wallets (out ↔ in).
 */

export type CgTransferMatch = {
  from: { walletId: string; walletName: string; symbol: string; direction: 'out'; amount: string; timestamp: string };
  to: { walletId: string; walletName: string; symbol: string; direction: 'in'; amount: string; timestamp: string };
};

/**
 * Likely internal transfers detected across all wallets (fetched on demand).
 */

export function useCryptoGestTransferMatches(enabled: boolean) {
  const client = useRequestProvider();

  return useQuery<CgTransferMatch[]>([KEY, 'transferMatches'], {
    enabled,
    queryFn: async () =>
      client.get(resolveAppUrl('cryptoGestTransferMatches')).then(({ data }: any) => data?.data ?? [])
  });
}

/**
 * Wallets list.
 */

export function useCryptoGestWallets() {
  const client = useRequestProvider();

  return useQuery<CgWallet[]>([KEY, 'wallets'], {
    queryFn: async () => client.get(resolveAppUrl('cryptoGestWallets')).then(({ data }: any) => data?.data ?? []),
    // While any wallet is syncing, poll so its card spinner clears on its own.
    refetchInterval: wallets => (wallets?.some(wallet => wallet.syncStatus === 'syncing') ? 2500 : false)
  });
}

/**
 * A single wallet (with holdings).
 */

export function useCryptoGestWallet(id?: string) {
  const client = useRequestProvider();

  return useQuery<CgWallet>([KEY, 'wallet', id], {
    enabled: !!id,
    queryFn: async () =>
      client.get(resolveAppUrl('cryptoGestWalletById', { interpolations: { id } })).then(({ data }: any) => data?.data)
  });
}

/**
 * Portfolio overview.
 */

export function useCryptoGestOverview() {
  const client = useRequestProvider();

  return useQuery<CgOverview>([KEY, 'overview'], {
    // Don't refetch on focus/mount — it remounts the charts and restarts their
    // animation, which reads as flicker/empty charts. Served from cache for a
    // minute; mutations still invalidate it explicitly.
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    queryFn: async () => client.get(resolveAppUrl('cryptoGestOverview')).then(({ data }: any) => data?.data)
  });
}

/**
 * Portfolio value reconstructed at interval buckets (back to the first entry).
 */

export function useCryptoGestValueHistory(interval = '12h') {
  const client = useRequestProvider();

  return useQuery<Array<{ timestamp: number; value: number }>>([KEY, 'valueHistory', interval], {
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    queryFn: async () =>
      client
        .get(resolveAppUrl('cryptoGestValueHistory', { params: { interval } }))
        .then(({ data }: any) => data?.data ?? [])
  });
}

/**
 * Asset detail.
 */

export function useCryptoGestAsset(assetId?: string | number) {
  const client = useRequestProvider();

  return useQuery<CgAssetDetail>([KEY, 'asset', String(assetId)], {
    enabled: assetId !== undefined && assetId !== null && `${assetId}` !== '',
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    queryFn: async () =>
      client.get(resolveAppUrl('cryptoGestAsset', { interpolations: { assetId } })).then(({ data }: any) => data?.data)
  });
}

/**
 * Per-asset paginated ledger (one wallet), filtered server-side. A row is either
 * a single entry or a folded, lazily-expandable reward run.
 */

export type CgAssetTxEntry = CgAssetWalletTx & { kind: 'entry' };

export type CgAssetRewardRun = {
  kind: 'rewardRun';
  walletId: string;
  count: number;
  from: string;
  to: string;
  amount: string;
};

export type CgAssetTxItem = CgAssetTxEntry | CgAssetRewardRun;

export type CgAssetTxPage = {
  rows: CgAssetTxItem[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
};

// `types`: null = all; [] = none; otherwise the selected types.
export function useCryptoGestAssetTransactions(
  assetId: string | number | undefined,
  params: { walletId: string; types: string[] | null; includePool: boolean; page: number; pageSize: number }
) {
  const client = useRequestProvider();
  const typesParam = params.types == null ? undefined : params.types.length ? params.types.join(',') : '-';

  return useQuery<CgAssetTxPage>(
    [
      KEY,
      'assetTx',
      String(assetId),
      params.walletId,
      typesParam ?? 'all',
      params.includePool,
      params.page,
      params.pageSize
    ],
    {
      enabled: assetId !== undefined && assetId !== null && `${assetId}` !== '' && !!params.walletId,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      queryFn: async () =>
        client
          .get(
            resolveAppUrl('cryptoGestAssetTransactions', {
              interpolations: { assetId },
              params: {
                wallet: params.walletId,
                includePool: params.includePool ? '1' : '0',
                page: params.page,
                pageSize: params.pageSize,
                ...(typesParam !== undefined ? { types: typesParam } : {})
              }
            })
          )
          .then(({ data }: any) => data?.data)
    }
  );
}

export function useCryptoGestAssetRewardRun(
  assetId: string | number | undefined,
  params: { walletId: string; from: string; to: string; includePool: boolean },
  enabled: boolean
) {
  const client = useRequestProvider();

  return useQuery<{ rows: CgAssetTxEntry[] }>(
    [KEY, 'assetRewardRun', String(assetId), params.walletId, params.from, params.to, params.includePool],
    {
      enabled: enabled && !!assetId && !!params.walletId && !!params.from && !!params.to,
      refetchOnWindowFocus: false,
      queryFn: async () =>
        client
          .get(
            resolveAppUrl('cryptoGestAssetRewardRun', {
              interpolations: { assetId },
              params: {
                wallet: params.walletId,
                from: params.from,
                to: params.to,
                includePool: params.includePool ? '1' : '0'
              }
            })
          )
          .then(({ data }: any) => data?.data)
    }
  );
}

/**
 * All ledger entries across the user's wallets (unified transactions view).
 */

export function useCryptoGestTransactions(walletId?: string) {
  const client = useRequestProvider();

  return useQuery<CgTransaction[]>([KEY, 'transactions', walletId ?? 'all'], {
    queryFn: async () =>
      client
        .get(resolveAppUrl('cryptoGestTransactions', { params: walletId ? { walletId } : undefined }))
        .then(({ data }: any) => data?.data ?? [])
  });
}

/**
 * Full price history for an asset at a given interval (Binance klines).
 */

export type CgPriceHistory = {
  symbol: string;
  interval: string;
  candles: Array<{ timestamp: number; price: number }>;
};

export function useCryptoGestAssetPriceHistory(
  assetId?: string | number,
  interval = '1d',
  startTime?: number,
  limit?: number
) {
  const client = useRequestProvider();

  return useQuery<CgPriceHistory>([KEY, 'assetPrices', String(assetId), interval, startTime ?? 'all', limit ?? 'max'], {
    enabled: assetId !== undefined && assetId !== null && `${assetId}` !== '',
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    queryFn: async () =>
      client
        .get(
          resolveAppUrl('cryptoGestAssetPriceHistory', {
            interpolations: { assetId },
            params: {
              interval,
              ...(startTime ? { startTime } : {}),
              ...(limit ? { limit } : {})
            }
          })
        )
        .then(({ data }: any) => data?.data)
  });
}

/**
 * Wallet ledger entries.
 */

export function useCryptoGestEntries(walletId?: string) {
  const client = useRequestProvider();

  return useQuery<CgEntry[]>([KEY, 'entries', walletId], {
    enabled: !!walletId,
    queryFn: async () =>
      client
        .get(resolveAppUrl('cryptoGestWalletEntries', { interpolations: { id: walletId } }))
        .then(({ data }: any) => data?.data ?? [])
  });
}

/**
 * A wallet's raw network transaction feed (read-only movements from sync).
 */

export function useCryptoGestOnchainTx(walletId?: string) {
  const client = useRequestProvider();

  return useQuery<CgOnchainTx[]>([KEY, 'onchain-tx', walletId], {
    enabled: !!walletId,
    queryFn: async () =>
      client
        .get(resolveAppUrl('cryptoGestWalletOnchainTx', { interpolations: { id: walletId } }))
        .then(({ data }: any) => data?.data ?? [])
  });
}

/**
 * Create a local ledger entry from a network transaction (marked "needs data").
 */

export function useCreateEntryFromOnchainTx() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async ({ walletId, txId }: { walletId: string; txId: number }) =>
      client
        .post(resolveAppUrl('cryptoGestOnchainTxCreateEntry', { interpolations: { id: walletId, txId } }))
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * User settings (display currency).
 */

export function useCryptoGestSettings() {
  const client = useRequestProvider();

  return useQuery<CgSettings>([KEY, 'settings'], {
    queryFn: async () => client.get(resolveAppUrl('cryptoGestSettings')).then(({ data }: any) => data?.data)
  });
}

/**
 * FX rates (USD per EUR + world currency rates per USD), refreshed periodically.
 */

export function useCryptoGestRates() {
  const client = useRequestProvider();

  return useQuery<CgRates>([KEY, 'rates'], {
    refetchInterval: 5 * 60 * 1000,
    queryFn: async () => client.get(resolveAppUrl('cryptoGestRates')).then(({ data }: any) => data?.data)
  });
}

/**
 * USD-per-EUR on a specific date (historical FX; falls back to the current rate
 * server-side). Cached per day.
 */

export function useCryptoGestFxAt(date?: string | null) {
  const client = useRequestProvider();
  const day = date ? String(date).slice(0, 10) : '';

  return useQuery<{ usdPerEur: number }>([KEY, 'fx', day], {
    enabled: !!day,
    staleTime: Infinity,
    queryFn: async () =>
      client.get(resolveAppUrl('cryptoGestFx', { params: { date: day } })).then(({ data }: any) => data?.data)
  });
}

/**
 * Update settings (display currency and/or the quick-picker currency list).
 */

export function useUpdateCryptoGestSettings() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { displayCurrency?: DisplayCurrency; currencies?: string[] }) =>
      client.patch(resolveAppUrl('cryptoGestSettings'), payload).then(({ data }: any) => data?.data),
    onSuccess: () => queryClient.invalidateQueries([KEY])
  });
}

/**
 * Persist a chart time-interval selector (value-over-time / price history).
 * Only the settings query is invalidated — the chart itself refetches off its
 * own interval key, so the rest of the section isn't refetched.
 */

export function useSaveCryptoGestChartInterval() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { valueInterval?: string; priceInterval?: string }) =>
      client.patch(resolveAppUrl('cryptoGestSettings'), payload).then(({ data }: any) => data?.data),
    onSuccess: () => queryClient.invalidateQueries([KEY, 'settings'])
  });
}

/**
 * Shared invalidator — refreshes the whole section after a mutation.
 */

function useInvalidate() {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries([KEY]);
}

/**
 * Create a wallet.
 */

export function useCreateCryptoGestWallet() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async (payload: any) =>
      client.post(resolveAppUrl('cryptoGestWallets'), payload).then(({ data }: any) => data?.data),
    onSuccess: () => {
      invalidate();

      // The API kicks off the first sync in the background right after create,
      // so refresh again shortly to surface the freshly pulled balances/history.
      setTimeout(invalidate, 4000);
    }
  });
}

/**
 * Import a Nexo transactions CSV into a nexo wallet.
 */

export function useImportNexoCsv() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async ({ id, csv, deleteOrphans }: { id: string; csv: string; deleteOrphans?: boolean }) =>
      client
        .post(resolveAppUrl('cryptoGestWalletNexoImport', { interpolations: { id } }), { csv, deleteOrphans })
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * Edit a wallet.
 */

export function useEditCryptoGestWallet() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & Record<string, any>) =>
      client
        .patch(resolveAppUrl('cryptoGestWalletById', { interpolations: { id } }), payload)
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * Delete a wallet.
 */

export function useDeleteCryptoGestWallet() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async (id: string) =>
      client
        .delete(resolveAppUrl('cryptoGestWalletById', { interpolations: { id } }))
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * Sync a tracked wallet now.
 */

export function useSyncCryptoGestWallet() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async (id: string) =>
      client
        .post(resolveAppUrl('cryptoGestWalletSync', { interpolations: { id } }))
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * Sync all of the user's tracked wallets at once.
 */

export function useSyncAllCryptoGestWallets() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async () => client.post(resolveAppUrl('cryptoGestWalletsSyncAll')).then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * Create a manual entry.
 */

export function useCreateCryptoGestEntry() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async ({ walletId, ...payload }: { walletId: string } & Record<string, any>) =>
      client
        .post(resolveAppUrl('cryptoGestWalletEntries', { interpolations: { id: walletId } }), payload)
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * Edit an entry (e.g. fill in the purchase value for a "needs data" one).
 */

export function useEditCryptoGestEntry() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async ({
      walletId,
      entryId,
      ...payload
    }: { walletId: string; entryId: string } & Record<string, any>) =>
      client
        .patch(resolveAppUrl('cryptoGestEntryById', { interpolations: { id: walletId, entryId } }), payload)
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * Delete an entry.
 */

export function useDeleteCryptoGestEntry() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async ({ walletId, entryId }: { walletId: string; entryId: string }) =>
      client
        .delete(resolveAppUrl('cryptoGestEntryById', { interpolations: { id: walletId, entryId } }))
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}
